import { useState } from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'

import Popover from 'modules/core/components/Popover'
import SimpleUserSelector from 'modules/core/components/SimpleUserSelector'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'

interface Props {
  additionalSigners: number[]
  addAdditionalSigners: (ids: number[]) => void
}

const SignersPopover = ({ additionalSigners, addAdditionalSigners }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const accessToDisplay = useUnsafeMemo(() => {
    return users.models.filter((user) => {
      const employee = user.isNew
        ? undefined
        : employees.find({
            access_id: user.attributes.id,
          })
      return user.isActive(employee) && user.attributes.confirmed
    })
  }, [])

  useUnsafeEffect(() => {
    setSelectedIds(additionalSigners)
  }, [isOpen, additionalSigners])

  const onSelect = (signerId: number | null | undefined) => {
    if (!signerId) return
    const newSelectedIds = selectedIds.includes(signerId)
      ? selectedIds.filter((id) => id !== signerId)
      : [...selectedIds, signerId]
    setSelectedIds(newSelectedIds)
  }

  const onClose = () => {
    setIsOpen(false)
    setSelectedIds([])
  }

  const onSubmit = () => {
    addAdditionalSigners(selectedIds)
    onClose()
  }

  return (
    <Popover
      overflowContent
      attachment="bottom center"
      open={isOpen}
      size="bigger"
      toggle={(value) => () => setIsOpen(value)}
      onOutsideClick={onClose}
    >
      <Box
        onClick={() => setIsOpen(true)}
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        padding="s12"
        gap="s8"
        borderRadius={{ all: 'abs008' }}
        border={{
          all: {
            width: 's2',
            style: 'dashed',
            color: 'grey400',
          },
        }}
        background="grey100"
      >
        <Icon.Medium icon="add" />
        <Text.Normal size="200" weight="semibold" color="grey800">
          {i18n.t('split_documents.form.include_additional_signer')}
        </Text.Normal>
      </Box>
      <Box padding="s32" gap="s16">
        <SimpleUserSelector
          loadingOptions={false}
          users={accessToDisplay}
          onSelect={(id) => onSelect(id)}
          onSelectMultiple={(ids) => addAdditionalSigners(ids)}
          selectedIds={selectedIds}
          autoSort
          size="medium"
          virtualized
          showControls={false}
        />
        <Button disabled={selectedIds.length === 0} onClick={onSubmit}>
          {selectedIds.length
            ? i18n.tp('split_documents.form.select_signers_button.selection', {
                count: selectedIds.length,
              })
            : i18n.t('split_documents.form.select_signers_button.none')}
        </Button>
      </Box>
    </Popover>
  )
}

export default SignersPopover
